import React, { useRef, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Divider,
    Box,
    Grid,
    List,
    ListItem,
    ListItemText,
    Button,
    CircularProgress
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import CategoryIcon from '@mui/icons-material/Category';
import SchoolIcon from "@mui/icons-material/School";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import htmlDocx from "html-docx-js/dist/html-docx";
import { saveAs } from "file-saver";
import { marked } from "marked";
import MarkedContent from "./MarkedContent/MarkedContent";
import { ViewContext } from "components/lib";
import { useContext } from "react";

export function ProjectReportPreview({ basicInfo = {}, chapters = [], references = [] }) {
    const viewContext = useContext(ViewContext);
    const reportRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const mdToHtml = (text = "") =>
        marked.parse(text, { breaks: true, mangle: false, headerIds: false });

    const handleDownloadReport = async () => {
        setLoading(true);
        try {
            let htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8" />
                    <style>
                    body {
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        font-size: 12pt;
                    }
                    p {
                        font-size: 12pt;
                        margin: 10px 0;
                        text-align: justify;
                        line-height: 1.6;
                    }
                    h1 {
                        font-size: 24pt;
                        font-weight: bold;
                        color: #1976d2;
                        margin: 20px 0;
                        text-align: center;
                    }
                    h2 {
                        font-size: 20pt;
                        font-weight: bold;
                        color: #1976d2;
                        margin: 20px 0 12px 0;
                        text-align: center;
                    }
                    h3 {
                        font-size: 16pt;
                        font-weight: bold;
                        margin: 15px 0 8px 0;
                    }
                    .center { 
                        text-align: center; 
                        margin: 8px 0;
                    }
                    .bold { font-weight: bold; }
                    hr {
                        border: none;
                        border-top: 1px solid #e0e0e0;
                        margin: 16px 0;
                    }
                    table {
                        width: 100%;
                        margin: 10px 0;
                        padding: 20px 0;
                        border-collapse: collapse;
                    }
                    td {
                        text-align: center;
                        padding: 8px;
                    }
                    ul, ol {
                        margin: 10px 0;
                        padding-left: 40px;
                        line-height: 1.6;
                    }
                    li {
                        margin: 6px 0;
                    }
                    ul ul {
                        list-style-type: circle;
                        padding-left: 30px;
                    }
                    code {
                        background-color: #f5f5f5;
                        padding: 2px 6px;
                        font-family: 'Courier New', monospace;
                    }
                    pre {
                        background-color: #f5f5f5;
                        padding: 12px;
                        white-space: pre-wrap;
                    }
                    blockquote {
                        border-left: 4px solid #ddd;
                        padding-left: 12px;
                        margin: 10px 0;
                        color: #666;
                    }
                    .section-header {
                        font-size: 20pt;
                        font-weight: bold;
                        color: #1976d2;
                        text-align: center;
                        margin: 30px 0 20px 0;
                        padding-left: 12px;
                        border-left: 4px solid #FFD700;
                        display: inline-block;
                    }
                    .bold{
                        font-weight: bold;
                    }
                    .section-wrapper {
                        text-align: center;
                        margin: 30px 0 20px 0;
                    }
                    @page {
                        @bottom-center {
                            content: "Page " counter(page) " of " counter(pages);
                        }
                        margin: 1in;
                    }
                    </style>
                </head>
                <body>
                    <h1 style="text-align: center; padding-top: 50px;">${basicInfo.title || "Untitled Project"}</h1>
                    <p style="text-align: center;font-size: 18px;font-weight: bold;margin-top: 40px;">Project Report</p>
                    <p style="text-align: center; margin-top: 40px;">Submitted for ${basicInfo.course || "Course Name"}</p>
                    <hr style="margin: 40px 0;"/>
                    <table>
                        <tr>
                            <td style="text-align: left;">👤 ${basicInfo.student || "Name"}</td>
                            <td style="text-align: right;">🆔 ${basicInfo.regNo || "Reg No"}</td>
                        </tr>
                        <tr>
                            <td style="text-align: left;">🗂️ ${basicInfo.projectType || "Project Type"}</td>
                            <td style="text-align: right;">👨‍🏫 ${basicInfo.instructor || "Instructor"}</td>
                        </tr>
                    </table>
                    <hr style="margin: 40px 0;"/>
                    <p style="text-align: center; margin-top: 40px;">🏛️ ${basicInfo.institution || "Institution Name"}</p>
                    <p style="text-align: center; margin-top: 40px;">📅 ${new Date().toLocaleDateString("en-GB")}</p>
                    
                    <br style="page-break-before: always;" />

                    <!-- ABSTRACT -->
                    <div class="section-wrapper">
                        <span class="section-header">Abstract</span>
                    </div>
                    ${basicInfo.abstract ? mdToHtml(basicInfo.abstract) : "<p>Abstract content will appear here once added.</p>"}
                    
                    <br style="page-break-before: always;" />

                    <!-- ACKNOWLEDGEMENTS -->
                    <div class="section-wrapper">
                        <span class="section-header">Acknowledgements</span>
                    </div>
                    ${basicInfo.acknowledgements ? mdToHtml(basicInfo.acknowledgements) : "<p>Acknowledgements content will appear here once added.</p>"}
                    
                    <br style="page-break-before: always;" />

                    <!-- TABLE OF CONTENTS -->
                    <div class="section-wrapper">
                        <span class="section-header">Table of Contents</span>
                    </div>
                    <ol>
                        <li>Abstract</li>
                        <li>Acknowledgements</li>`;

            chapters.forEach((chapter) => {
                htmlContent += `<li>${chapter.title}`;
                if (chapter.sections?.length > 0) {
                    htmlContent += `<ul>`;
                    chapter.sections.forEach(section => {
                        htmlContent += `<li>${section.title}</li>`;
                    });
                    htmlContent += `</ul>`;
                }
                htmlContent += `</li>`;
            });

            if (references.length > 0) {
                htmlContent += `<li>References</li>`;
            }

            htmlContent += `</ol>`;

            chapters.forEach((chapter) => {
                htmlContent += `
                    <br style="page-break-before: always;" />
                    <h2>${chapter.title}</h2>
                    ${chapter.content ? mdToHtml(chapter.content) : ""}`;

                if (chapter.sections?.length > 0) {
                    chapter.sections.forEach((section) => {
                        htmlContent += `
                            <h3>${section.title}</h3>
                            ${section.content ? mdToHtml(section.content) : ""}`;
                    });
                }
            });

            if (references.length > 0) {
                htmlContent += `
                    <br style="page-break-before: always;" />
                    <div class="section-wrapper">
                        <span class="section-header">References</span>
                    </div>
                    <ul>`;
                references.forEach((ref) => {
                    htmlContent += `<li>${ref.author} (${ref.year}). ${ref.title}. ${ref.publisher}.</li>`;
                });
                htmlContent += `</ul>`;
            }

            htmlContent += `</body></html>`;

            const blob = htmlDocx.asBlob(htmlContent);
            saveAs(blob, `${basicInfo.title || "Project_Report"}.docx`);
        } catch (err) {
            console.error("Error generating Word document", err);
        } finally {
            setLoading(false);
        }
    };

    const renderChapterContent = (chapter) => (
        <Box key={chapter.title} sx={{ mb: 3 }}>
            <Typography
                variant="h5"
                fontWeight="bold"
                color="primary"
                gutterBottom
                align="center"
            >
                {chapter.title}
            </Typography>
            {chapter.content && (
                <MarkedContent content={chapter.content} justify />
            )}
            {chapter.sections?.map((section, idx) => (
                <Box key={idx} sx={{ ml: 2, mb: 2 }}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ mb: 1 }}
                        align="left"
                    >
                        {section.title}
                    </Typography>
                    {section.content && <MarkedContent content={section.content} justify />}
                </Box>
            ))}
        </Box>
    );

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "0px 10px",
                    alignItems: "center",
                    paddingBottom: 2,
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e5e7eb",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                }}
            >
                <Box display="flex" alignItems="center">
                    <ArticleIcon sx={{ color: "#000000", mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                        {viewContext.t('Components.ProjectCommon.preview.previewTitle')}
                    </Typography>
                </Box>
                <Button
                    sx={{
                        margin: '0px 10px',
                        marginTop: '8px',
                        backgroundColor: "#1450a0",
                        borderRadius: '8px',
                        "&:hover": { backgroundColor: "#165bb6" }
                    }}
                    startIcon={
                        loading ? <CircularProgress size={18} color="inherit" /> : null
                    }
                    variant="contained"
                    disabled={loading}
                    onClick={handleDownloadReport}
                >
                    {loading ? viewContext.t('Components.ProjectCommon.button.downloading') : viewContext.t('Components.ProjectCommon.button.download')}
                </Button>
            </Box>

            <Card
                ref={reportRef}
                sx={{
                    borderRadius: "16px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    p: 2,
                    backgroundColor: "#fff",
                }}
            >
                <CardContent>
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        color="primary"
                        gutterBottom
                        align="center"
                    >
                        {basicInfo.title || "Untitled Project"}
                    </Typography>

                    <Typography variant="h6" align="center" gutterBottom>
                        <b>Project Report</b>
                    </Typography>
                    <Typography
                        variant="body1"
                        align="center"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ mb: 3, marginBottom: 6 }}
                    >
                        Submitted for {basicInfo.course || "Course Name"}
                    </Typography>


                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={1} justifyContent="center">
                        <Grid item xs={12} sm={5} display="flex" justifyContent="left">
                            <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                {basicInfo.student || "Name"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={5} display="flex" justifyContent="right">
                            <PersonAddAltIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                {basicInfo.regNo || "Reg No"}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid container spacing={1} justifyContent="center" mb={2} marginTop={3}>
                        <Grid item xs={12} sm={5} display="flex" justifyContent="left">
                            <AccountBalanceIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                {basicInfo.projectType || "Name"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={5} display="flex" justifyContent="right">
                            <SchoolIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                {basicInfo.instructor || "Name"}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="center" alignItems="center" marginTop={6}>
                        <CategoryIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                            {basicInfo.institution || "Name"}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="center" alignItems="center" marginTop={3}>
                        <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                            {new Date().toLocaleDateString("en-GB")}
                        </Typography>
                    </Box>

                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="primary"
                        gutterBottom
                        sx={{ borderLeft: "4px solid gold", pl: 1, marginTop: 4 }}
                    >
                        Abstract
                    </Typography>
                    <Typography style={{ textAlign: "justify" }} variant="body2" mb={2}>
                        {basicInfo.abstract ||
                            "Abstract content will appear here once added."}
                    </Typography>

                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="primary"
                        gutterBottom
                        sx={{ borderLeft: "4px solid gold", pl: 1 }}
                    >
                        Acknowledgements
                    </Typography>
                    <Typography style={{ textAlign: "justify" }} variant="body2" mb={2}>
                        {basicInfo.acknowledgements ||
                            "Acknowledgements content will appear here once added."}
                    </Typography>

                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="primary"
                        gutterBottom
                        sx={{ borderLeft: "4px solid gold", pl: 1 }}
                    >
                        Table of Contents
                    </Typography>
                    <List component="ol" dense sx={{ listStyleType: "decimal", pl: 4 }}>
                        <ListItem component="li" sx={{ display: "list-item" }}>
                            <ListItemText primary="Abstract" />
                        </ListItem>
                        <ListItem component="li" sx={{ display: "list-item" }}>
                            <ListItemText primary="Acknowledgements" />
                        </ListItem>

                        {chapters.map((chapter, i) => (
                            <Box key={i}>
                                <ListItem component="li" sx={{ display: "list-item" }}>
                                    <ListItemText primary={chapter.title} />
                                </ListItem>

                                {chapter.sections?.length > 0 && (
                                    <List
                                        component="ul"
                                        dense
                                        sx={{ listStyleType: "circle", pl: 4 }}
                                    >
                                        {chapter.sections.map((s, j) => (
                                            <ListItem
                                                key={j}
                                                component="li"
                                                sx={{ display: "list-item" }}
                                            >
                                                <ListItemText primary={s.title} />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        ))}

                        {references.length > 0 && (
                            <ListItem component="li" sx={{ display: "list-item" }}>
                                <ListItemText primary="References" />
                            </ListItem>
                        )}
                    </List>
                    <Divider sx={{ my: 2 }} />

                    {chapters.map((chapter) => renderChapterContent(chapter))}

                    {references.length > 0 && (
                        <>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                color="primary"
                                gutterBottom
                                align="center"
                            >
                                References
                            </Typography>
                            <List dense>
                                {references.map((ref, index) => (
                                    <ListItem key={index}>
                                        <ListItemText
                                            primary={`${ref.author} (${ref.year}). ${ref.title}. ${ref.publisher}.`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </CardContent>
            </Card>
        </>
    );
}